import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { MotionConfig } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Navbar from './components/Navbar';
import { SkeletonPage } from './components/Skeleton';
import ErrorBoundary from './components/ErrorBoundary';

// LandingPage stays a normal (eager) import — it's what every anonymous
// visitor needs first, so it shouldn't wait on a lazy chunk + Suspense
// flash. Every other page is lazy() so visiting "/" doesn't pull in
// react-globe.gl (Dashboard's 3D globe), recharts, or any other page's
// code — before this, App.js imported all 24 pages eagerly, so the landing
// page paid for the entire app's JS (over 1MB gzipped, per a production
// build) before it could render a single pixel. On a memory-constrained
// in-app browser (WhatsApp/Instagram's embedded WebView) that's slow at
// best and can fail outright with nothing shown but the raw HTML
// background — see ErrorBoundary.js for what used to happen when it did.
import LandingPage from './pages/LandingPage';
import Maintenance from './pages/Maintenance';
const TrendingToday       = lazy(() => import('./pages/TrendingToday'));
const StockDetail         = lazy(() => import('./pages/StockDetail'));
const AIInsights          = lazy(() => import('./pages/AIInsights'));
const MonthlySeasonality  = lazy(() => import('./pages/MonthlySeasonality'));
const MacroInsights       = lazy(() => import('./pages/MacroInsights'));
// Quant disabled 2026-09-05 — fetches from Yahoo Finance, pending the
// licensing review (see Stock-AI-Backend/src/app.js's quantRouter comment).
// const QuantAnalysis       = lazy(() => import('./pages/QuantAnalysis'));
const StrategyScreener    = lazy(() => import('./pages/StrategyScreener'));
const Pricing             = lazy(() => import('./pages/Pricing'));
const Watchlist           = lazy(() => import('./pages/Watchlist'));
const PortfolioDoctor     = lazy(() => import('./pages/PortfolioDoctor'));
const PrivacyPolicy       = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse          = lazy(() => import('./pages/TermsOfUse'));
const Disclaimer          = lazy(() => import('./pages/Disclaimer'));
const AboutUs             = lazy(() => import('./pages/AboutUs'));
const Mentorship          = lazy(() => import('./pages/Mentorship'));
// FPI Tracker disabled 2026-09-05 — pending NSDL licensing review (see
// Stock-AI-Backend/src/services/fpiNsdl.js header comment). Restore this
// import + the /fpi route below once NSDL permission is confirmed or a
// licensed vendor replaces this source.
// const FPITracker          = lazy(() => import('./pages/FPITracker'));
const MonthlyPlaybook     = lazy(() => import('./pages/MonthlyPlaybook'));
const StockResearch       = lazy(() => import('./pages/StockResearch'));
const CompareCompanies    = lazy(() => import('./pages/CompareCompanies'));
// Market Intelligence disabled 2026-09-05 — its Sector Cycles tab fetches
// per-stock data from Yahoo Finance (via sectorStrength.js/sectorEngine.js),
// and its Macro tab duplicates the standalone /macro page (MacroInsights.js)
// which stays live. Restore this import + the /market-intelligence route +
// Navbar entry to bring it back.
// const MarketIntelligence  = lazy(() => import('./pages/MarketIntelligence'));
// Research Lab disabled 2026-09-05, hidden for now (see
// Stock-AI-Backend/src/app.js's researchLabRouter comment). Restore this
// import + the /research-lab route + Navbar entry to bring it back. NOTE:
// MyResearch.js's "rerun a saved study" links point to /research-lab — they
// will 404 while this is hidden (MyResearch is itself still nav-flagged
// comingSoon, so this is low-visibility, not fixed separately here).
// const ResearchLab         = lazy(() => import('./pages/ResearchLab'));
const MyResearch          = lazy(() => import('./pages/MyResearch'));
// Admin-only ops page for the BSE data-pipeline (../../data-pipeline). Not
// linked from Navbar — gated by ADMIN_SECRET (X-Admin-Key), not the user
// JWT, so it's deliberately outside ProtectedRoute below.
const DataPipeline        = lazy(() => import('./pages/DataPipeline'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const APP_FOOTER_LINKS = [
  ['Privacy', '/privacy'],
  ['Terms',   '/terms'],
  ['Disclaimer', '/disclaimer'],
  ['About',   '/about'],
  ['Mentorship', '/mentorship'],
];

function AppLayout({ children }) {
  const { colors, fonts } = useTheme();
  const C = colors;

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        // Deepest canvas — one tier below the Forest nav/sidebar chrome so
        // the app reads as layered (page < nav/cards < nested), not flat.
        background: C.bgDeep,
        color: C.text,
        fontFamily: fonts.body,
        transition: 'background 0.3s, color 0.3s',
      }}
    >
      <Navbar />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: C.surface,
            color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: fonts.display,
          },
        }}
      />
      {/* Global page width — 1800px, not the older 1280px (max-w-7xl): on a
          wide monitor 1280px reads as two large empty gutters either side
          of the content, which is the opposite of how a data-dense app
          should use a wide screen. Pages that intentionally want a
          narrower, prose-comfortable column (landing/marketing/legal
          pages, Research Lab's single-column form) set their own tighter
          `maxWidth` one level down, inside this — this cap is only a
          ceiling for pages that don't. */}
      <main className="max-w-[1800px] mx-auto px-4 py-6" style={{ flex: 1, width: '100%' }}>
        {children}
      </main>

      {/* Minimal post-login footer */}
      <footer style={{
        borderTop: `1px solid ${C.border}`,
        padding: '0.875rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.5rem',
        fontFamily: fonts.display,
      }}>
        <p style={{ color: C.textMuted, fontSize: '0.68rem', margin: 0, opacity: 0.5 }}>
          © 2026 MarketNerve · For informational purposes only. Not financial advice.
        </p>
        <nav style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
          {APP_FOOTER_LINKS.map(([label, href]) => (
            <Link key={href} to={href} style={{
              color: C.textMuted, fontSize: '0.68rem', textDecoration: 'none',
              opacity: 0.6, transition: 'opacity 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
            >
              {label}
            </Link>
          ))}
        </nav>
      </footer>
    </div>
  );
}

// React Router doesn't reset scroll position on navigation (that's normal
// multi-page-site behavior, not SPA behavior) — without this, clicking a
// link while scrolled down on one page lands you on the *next* page still
// scrolled down, showing its middle/bottom first. Keyed on pathname only
// (not search/hash), so it doesn't fight in-page anchor links like the
// landing page's "#features".
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="max-w-[1800px] mx-auto px-4 py-6"><SkeletonPage /></div>;
  // No dedicated /login page anymore — bounce to the landing page and tell
  // it to pop the sign-in modal open over the hero.
  if (!user) return <Navigate to="/" state={{ openLogin: true }} replace />;
  // Suspense sits *inside* AppLayout, around just the page content — so the
  // Navbar/footer chrome stays mounted while a lazy page chunk loads instead
  // of the whole layout flashing out to a bare skeleton.
  return (
    <AppLayout>
      <Suspense fallback={<SkeletonPage />}>
        {children}
      </Suspense>
    </AppLayout>
  );
}

function AppRoutes() {
  return (
    // Outer Suspense is the fallback net for the lazy *public* pages
    // (Pricing, Privacy, Terms, …) that aren't behind ProtectedRoute and so
    // don't get the inner one above.
    <Suspense fallback={<div className="max-w-[1800px] mx-auto px-4 py-6"><SkeletonPage /></div>}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/mentorship" element={<Mentorship />} />

      {/* Dashboard under maintenance (2026-11-05) — swapped to the Maintenance
          page, still inside ProtectedRoute so the Navbar (and its other tabs)
          and footer stay visible/usable — only the dashboard content itself
          is down. Restore <TrendingToday /> in place of <Maintenance />
          once the dashboard is back. */}
      <Route path="/dashboard"    element={<ProtectedRoute><Maintenance /></ProtectedRoute>} />
      {/* Free/Pro restructuring, Phase 1 — Decision 2 (MARKETNERVE_FREE_PRO_AUDIT.md
          §6): cutover completed. StockResearch.js (all 13+ engine modules,
          Free/Pro gating applied per-module starting with Valuation) is now
          the live page at the canonical route. StockDetail.js is kept
          mounted at its old route, unlinked from the rest of the app, as a
          fallback until this is visually verified on localhost — not
          deleted outright per the "verify before removing" rule. */}
      <Route path="/stock/:symbol" element={<ProtectedRoute><StockResearch /></ProtectedRoute>} />
      <Route path="/stock-research/:symbol" element={<ProtectedRoute><StockResearch /></ProtectedRoute>} />
      <Route path="/stock-detail-legacy/:symbol" element={<ProtectedRoute><StockDetail /></ProtectedRoute>} />
      <Route path="/compare/:symbols" element={<ProtectedRoute><CompareCompanies /></ProtectedRoute>} />
      <Route path="/ai-insights"  element={<ProtectedRoute><AIInsights /></ProtectedRoute>} />
      <Route path="/seasonality"  element={<ProtectedRoute><MonthlySeasonality /></ProtectedRoute>} />
      <Route path="/macro"        element={<ProtectedRoute><MacroInsights /></ProtectedRoute>} />
      {/* /quant disabled 2026-09-05 — see the QuantAnalysis import comment above */}
      {/* <Route path="/quant"        element={<ProtectedRoute><QuantAnalysis /></ProtectedRoute>} /> */}
      <Route path="/strategy"     element={<ProtectedRoute><StrategyScreener /></ProtectedRoute>} />
      <Route path="/watchlist"    element={<ProtectedRoute><Watchlist /></ProtectedRoute>} />
      <Route path="/portfolio"    element={<ProtectedRoute><PortfolioDoctor /></ProtectedRoute>} />
      {/* /fpi disabled 2026-09-05 — see the FPITracker import comment above */}
      {/* <Route path="/fpi"          element={<ProtectedRoute><FPITracker /></ProtectedRoute>} /> */}
      {/* /market-intelligence disabled 2026-09-05 — see the MarketIntelligence import comment above */}
      {/* <Route path="/market-intelligence" element={<ProtectedRoute><MarketIntelligence /></ProtectedRoute>} /> */}
      {/* /research-lab disabled 2026-09-05 — see the ResearchLab import comment above */}
      {/* <Route path="/research-lab" element={<ProtectedRoute><ResearchLab /></ProtectedRoute>} /> */}
      <Route path="/my-research" element={<ProtectedRoute><MyResearch /></ProtectedRoute>} />
      <Route path="/playbook"     element={<ProtectedRoute><MonthlyPlaybook /></ProtectedRoute>} />

      {/* Admin-only — key-gated inside the page itself, not by ProtectedRoute */}
      <Route path="/admin/pipeline" element={<DataPipeline />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    // ErrorBoundary is the outermost thing that can render: if anything
    // below throws during mount (a lazy chunk failing to evaluate, a
    // WebGL/three.js init issue on a constrained device, anything) this is
    // what stands between that and a silently blank page — see
    // ErrorBoundary.js for what happens without it.
    <ErrorBoundary>
      {/* reducedMotion="user" makes every Framer Motion component in the app
          automatically skip transform/layout animation for users with
          OS-level "reduce motion" enabled, without each page needing its
          own check. */}
      <MotionConfig reducedMotion="user">
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <AuthProvider>
            <ThemeProvider>
              <Router>
                <ScrollToTop />
                <AppRoutes />
              </Router>
            </ThemeProvider>
          </AuthProvider>
        </GoogleOAuthProvider>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;
